import { Skeleton } from "@/components/atoms";
import { Button } from "@/components/atoms/Button";
import { Card } from "@/components/atoms/Card";
import { ChevronRightIcon } from "@/components/atoms/icons/ChevronRightIcon";
import { CircleCheckIcon } from "@/components/atoms/icons/CircleCheckIcon";
import { CircleIcon } from "@/components/atoms/icons/CircleIcon";
import { Typography } from "@/components/atoms/Typography";
import { LogoHeader } from "@/components/molecules/header/LogoHeader";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore } from "@/core/stores/appStore";
import { isSmallScreen } from "@/core/utils/responsive";
import { router } from "expo-router";
import React from "react";
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useServiceRequests } from "../hooks/useServiceRequests";
import { ServiceRequestDto } from "../services/serviceRequestService";
import { ServiceRequestScreenMobile } from "./ServiceRequestScreenMobile";

export interface ServiceRequest {
    id: string;
    category: string;
    title: string;
    status: string;
    isClosed: boolean;
    createdAt: string;
    caseNumber?: string;
    description?: string;
    unitName?: string | null;
}

interface RequestHistoryItemProps {
    readonly request: ServiceRequest;
    readonly onPress?: () => void;
    /** compact = stacked layout (mobile web / narrow); wide = row with status beside title (desktop) */
    readonly variant?: 'compact' | 'wide';
    readonly inProgressLabel: string;
}

function RequestHistoryItem({ request, onPress, variant = 'compact', inProgressLabel }: RequestHistoryItemProps) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();

    const isResolved = request.isClosed;

    const statusRow = (
        <View style={styles.statusContainer}>
            {isResolved ? (
                <CircleCheckIcon size={12} color={colors.fontSuccessText} />
            ) : (
                <CircleIcon size={12} color={colors.fontTertiary} />
            )}
            <Typography
                variant="footnote"
                style={{
                    color: isResolved ? colors.fontSuccessText : colors.fontTertiary,
                }}
            >
                {request.status || inProgressLabel}
            </Typography>
        </View>
    );

    const chevron = (
        <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
            <ChevronRightIcon size={24} color={colors.fontPrimary} />
        </View>
    );

    return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
            <Card
                variant="default"
                padding={16}
                style={{
                    backgroundColor: colors.white,
                    borderColor: colors.outlineInactive,
                    borderRadius: 12,
                    borderWidth: 1,
                    shadowColor: 'transparent',
                    shadowOffset: { width: 0, height: 0 },
                    shadowOpacity: 0,
                    shadowRadius: 0,
                    elevation: 0,
                }}
            >
                {variant === 'compact' ? (
                    <View style={styles.historyItemContent}>
                        <View style={[styles.historyItemText, styles.historyItemTextCompact]}>
                            <Typography
                                variant="body"
                                color="primary"
                                style={styles.historyText}
                            >
                                {request.category}
                            </Typography>
                            <Typography
                                variant="headline"
                                color="primary"
                                numberOfLines={2}
                                style={styles.historyText}
                            >
                                {request.title}
                            </Typography>
                            {statusRow}
                        </View>
                        {chevron}
                    </View>
                ) : (
                    <View style={styles.historyItemContentWide}>
                        <View style={styles.historyItemTextWide}>
                            <Typography variant="body" color="primary">
                                {request.category}
                            </Typography>
                            <Typography variant="headline" color="primary" numberOfLines={1}>
                                {request.title}
                            </Typography>
                        </View>
                        <View style={styles.historyStatusCenter} pointerEvents="none">
                            <View style={styles.historyStatusInner}>{statusRow}</View>
                        </View>
                        <View style={styles.historyChevronWide}>{chevron}</View>
                    </View>
                )}
            </Card>
        </TouchableOpacity>
    );
}

function ServiceRequestScreenWeb() {
    useCatalogDynatraceView(DT_SCREEN.SERVICE_REQUEST, 'service_request_list_viewed', true);
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const user = useAppStore((state) => state.auth.user);
    const { data, isLoading, isError } = useServiceRequests();
    const isLargeScreen = !isSmallScreen();

    const displayName =
        user?.full_name ||
        user?.firstnameEN ||
        user?.name ||
        t('support.guestNameFallback');

    const handleRaiseRequest = () => {
        logger.debug('ServiceRequestScreen', 'Raise request pressed');
        try {
            router.push('/service-request-create');
        } catch (e) {
            logger.warn('ServiceRequestScreen', 'Navigation to create request failed', e);
        }
    };

    const handleRequestPress = (request: ServiceRequest) => {
        logger.debug('ServiceRequestScreen', 'Request pressed', { requestId: request.id });
        trackCatalogEvent({
            screen: DT_SCREEN.SERVICE_REQUEST,
            actionName: 'service_request_open_detail',
            eventType: 'action',
            properties: { requestId: request.id },
        });
        try {
            router.push({
                pathname: '/service-request/[id]',
                params: {
                    id: request.id,
                    title: request.title,
                    category: request.category,
                    status: request.status,
                    createdAt: request.createdAt,
                    caseNumber: request.caseNumber ?? '',
                    description: request.description ?? '',
                    unit: request.unitName ?? '',
                },
            } as never);
        } catch (e) {
            logger.warn('ServiceRequestScreen', 'Navigation failed', e);
        }
    };

    const mappedRequests: ServiceRequest[] =
        data?.map((item: ServiceRequestDto) => ({
            id: item.Id,
            category: item.subject || item.caseNumber || '',
            title: item.description || item.subject || item.caseNumber || '',
            status: item.status || item.substatus || t('support.inProgress'),
            isClosed: (item.status || '').toLowerCase() === 'closed',
            createdAt: item.created_on,
            caseNumber: item.caseNumber,
            description: item.description,
            unitName: item.unitName,
        })) || [];

    const showSkeleton = isLoading && !mappedRequests.length;

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            <LogoHeader
                showLanguageSwitch={false}
                hideLogo={true}
            />

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={[
                    styles.scrollContent,
                    { paddingHorizontal: isLargeScreen ? 24 : 16 },
                ]}
                showsVerticalScrollIndicator={false}
            >
                {/* Header Section */}
                <View style={styles.headerSection}>
                    {isLargeScreen ? (
                        <>
                            <View style={styles.headerTopRow}>
                                <Typography
                                    variant="largeTitle"
                                    color="primary"
                                    style={[styles.title, { textAlign: isRTL ? 'right' : 'left' }]}
                                >
                                    {t('support.title')}
                                </Typography>
                                <Button
                                    title={t('support.raiseRequest')}
                                    onPress={handleRaiseRequest}
                                    variant="primary"
                                    style={styles.raiseRequestButton}
                                />
                            </View>
                            <Typography variant="title3" color="primary" style={styles.greeting}>
                                {t('support.subtitle').replace('{{name}}', displayName)}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="largeTitle"
                                color="primary"
                                style={[styles.title, { textAlign: isRTL ? 'right' : 'left' }]}
                            >
                                {t('support.title')}
                            </Typography>
                            <Typography variant="title3" color="primary" style={styles.greeting}>
                                {t('support.subtitle').replace('{{name}}', displayName)}
                            </Typography>
                        </>
                    )}
                </View>

                {!isLargeScreen && (
                    <Button
                        title={t('support.raiseRequest')}
                        onPress={handleRaiseRequest}
                        variant="primary"
                        fullWidth
                    />
                )}

                {/* History Section */}
                <View style={styles.historySection}>
                    <Typography
                        variant="title3"
                        color="primary"
                        style={[styles.historyTitle, { textAlign: isRTL ? 'right' : 'left' }]}
                    >
                        {t('support.history')}
                    </Typography>

                    <View
                        style={[
                            styles.historyList,
                            !isLargeScreen && styles.historyListCompact,
                        ]}
                    >
                        {showSkeleton
                            ? (['sk-1', 'sk-2', 'sk-3'] as const).map((id) => (
                                  <Skeleton key={id} height={96} borderRadius={16} />
                              ))
                            : mappedRequests.map((request) => (
                                  <RequestHistoryItem
                                      key={request.id}
                                      request={request}
                                      variant={isLargeScreen ? 'wide' : 'compact'}
                                      inProgressLabel={t('support.inProgress')}
                                      onPress={() => handleRequestPress(request)}
                                  />
                              ))}
                        {!showSkeleton && !mappedRequests.length && !isLoading && (
                            <View style={styles.emptyState}>
                                <Typography variant="body" color="tertiary">
                                    {isError ? t('common.error') : t('support.emptyState')}
                                </Typography>
                            </View>
                        )}
                    </View>
                </View>
            </ScrollView>
        </View>
    );
}

export function ServiceRequestScreen() {
    if (Platform.OS === 'web') {
        return <ServiceRequestScreenWeb />;
    }
    return <ServiceRequestScreenMobile />;
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingTop: 8,
        paddingBottom: 32,
        gap: 32,
    },
    headerSection: {
        gap: 16,
    },
    headerTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    title: {
        fontSize: 28,
        lineHeight: 40,
        fontWeight: '500',
        flex: 1,
    },
    greeting: {
        lineHeight: 26,
    },
    raiseRequestButton: {
        flexShrink: 0,
    },
    historySection: {
        gap: 16,
    },
    historyTitle: {
        fontSize: 20,
        lineHeight: 28,
        fontWeight: '500',
    },
    historyList: {
        gap: 16,
    },
    historyListCompact: {
        gap: 16,
    },
    emptyState: {
        paddingVertical: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    historyItemContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    historyItemContentWide: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
    },
    historyItemText: {
        flex: 1,
        gap: 4,
        minWidth: 0,
    },
    historyItemTextWide: {
        flex: 1,
        gap: 8,
        minWidth: 0,
        paddingRight: 16,
        zIndex: 1,
    },
    historyStatusCenter: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 0,
    },
    historyStatusInner: {
        width: 120,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    historyChevronWide: {
        flexShrink: 0,
        zIndex: 1,
    },
    historyItemTextCompact: {
        gap: 8,
    },
    historyText: {
        lineHeight: 22,
    },
    statusContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
    },
});

export default ServiceRequestScreen;

